import { authService } from '@/api/services/authService';
import { isSessionTokenValid } from '@/features/auth/authSession';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface AuthContextValue {
  isAuthenticated: boolean;
  userId: string | null;
  logout: () => void;
  refresh: () => void;
}

interface AuthSnapshot {
  isAuthenticated: boolean;
  userId: string | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function readAuthSnapshot(): AuthSnapshot {
  const token = authService.getToken();
  return {
    userId: authService.getUserId(),
    isAuthenticated: isSessionTokenValid(token),
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [authState, setAuthState] = useState<AuthSnapshot>(readAuthSnapshot);

  const refresh = useCallback(() => {
    setAuthState(readAuthSnapshot());
  }, []);

  useEffect(() => {
    const onAuthChange = () => refresh();
    window.addEventListener('storage', onAuthChange);
    window.addEventListener('fintecy-auth-change', onAuthChange);
    return () => {
      window.removeEventListener('storage', onAuthChange);
      window.removeEventListener('fintecy-auth-change', onAuthChange);
    };
  }, [refresh]);

  const logout = useCallback(() => {
    authService.removeToken();
    authService.removeUserInfo();
    queryClient.clear();
    navigate('/login', { replace: true });
  }, [navigate, queryClient]);

  const value = useMemo(
    () => ({
      isAuthenticated: authState.isAuthenticated,
      userId: authState.userId,
      logout,
      refresh,
    }),
    [authState.isAuthenticated, authState.userId, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
